// 音声エージェント（Gemini Live）の system instruction 組み立て（純関数・ブラウザ非依存）。
//
// 役割: Gemini に「自分は空間処理を実行せず、ユーザーと会話して Claude への指示文を練る相棒だ」と
//       伝える文面を作る。現在のレイヤーとデータテーブルを材料として一緒に渡す。
// 関係: useVoiceSession が接続時に渡す。会話中のレイヤー増減は buildContextSnapshot() を
//       ツール応答へ同梱して伝える（会話中に text を送るとモデルが喋り出してしまうため）。
//       データテーブルの整形は Claude 側と同じ formatDataTablesForPrompt を再利用する。
import { formatDataTablesForPrompt } from '../data/data-prompt.js'

const BASE = `あなたは「web-gis-ai-agent」という地理空間分析アプリの音声アシスタントです。

## このアプリについて
- ブラウザだけで動く GIS ワークベンチです。DuckDB-WASM の spatial 拡張で空間 SQL を実行し、結果を新しい地図レイヤーとして追加します。
- 実際の処理を行うのは、このアプリに内蔵された **Claude エージェント**です。ユーザーが画面のプロンプト入力欄に指示文を入れて送信すると、Claude が SQL を組み立てて実行します。
- Claude ができること: ファイル取込（GeoJSON / FlatGeobuf / GeoParquet / CSV）、バッファや交差などの空間処理、非空間 CSV とのコード結合（JOIN）、属性値による塗り分け（コロプレス）、geoBoundaries からの行政界取得、GeoJSON / GeoParquet でのエクスポート。

## あなたの役割
- あなた自身はデータを処理しません。**ユーザーと会話して、Claude に渡す指示文を一緒に練る**のが仕事です。
- やりたいことが曖昧なときは、対象データ・処理内容・出力の3点が埋まるまで質問してください。
- 指示文がまとまったら fill_prompt を呼んで入力欄に書き込みます。**書き込むだけで送信はされません。** 送信ボタンを押すのはユーザーです。書き込んだら「入力しました。内容を確認して送信ボタンを押してください」と伝えてください。
- 地図の見た目を確認しないと答えられないときだけ capture_map を呼びます。連続では呼ばないでください。
- レイヤーやデータの名前は、下の一覧にある ID をそのまま使ってください。一覧にないデータが必要な場合は、ユーザーに取込を促してください。

## 話し方
- 日本語で話します。
- 音声なので短く、1 度に 1 つだけ質問します。長い説明や箇条書きの読み上げは避けてください。
- 専門用語は噛み砕いて説明します。`

// レイヤー一覧を音声アシスタント向けに整形する。
// Claude 側（useAgentSession.buildSystemWithLayers）と同じ粒度に、表示状態と塗り分けを足している。
export function formatLayersForVoice(layers = []) {
  if (!layers.length) return '（まだレイヤーはありません）'
  return layers
    .map((l) => {
      const parts = [`${l.geomType ?? '?'}`, `${l.featureCount ?? '?'} 件`]
      if (l.visible === false) parts.push('非表示')
      if (l.style?.type === 'choropleth') parts.push(`${l.style.column} で塗り分け中`)
      const name = l.displayName && l.displayName !== l.layerId ? `${l.displayName} / ` : ''
      return `- ${name}${l.layerId}（${parts.join(', ')}）`
    })
    .join('\n')
}

// 現在のレイヤー / データテーブルの状況ブロック。接続時と、途中の変化通知の両方で使う。
export function buildContextBlock({ layers = [], dataTables = [] } = {}) {
  const blocks = [`## 現在のレイヤー\n${formatLayersForVoice(layers)}`]
  const data = formatDataTablesForPrompt(dataTables)
  if (data) blocks.push(data)
  return blocks.join('\n\n')
}

// 接続時に渡す system instruction 全文。
export function buildVoiceInstruction({ layers = [], dataTables = [] } = {}) {
  return `${BASE}\n\n${buildContextBlock({ layers, dataTables })}`
}

// ツール応答へ同梱する現在の状況。会話中に text を送るとモデルが喋り出してしまうため、
// レイヤーの変化は「Gemini が関数を呼んだときの応答」に載せて最新化する。
export function buildContextSnapshot({ layers = [], dataTables = [], isAgentRunning = false } = {}) {
  return {
    layers: layers.map((l) => ({
      id: l.layerId,
      name: l.displayName,
      geom: l.geomType,
      count: l.featureCount,
      visible: l.visible !== false,
      choropleth: l.style?.type === 'choropleth' ? l.style.column : undefined,
    })),
    data_tables: dataTables.map((t) => ({ id: t.tableId, rows: t.rowCount })),
    // Claude 実行中は送信しても待たされるので、その旨を伝えられるようにする。
    claude_running: isAgentRunning,
  }
}
